import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Smile, Meh, Frown, Heart, Flame, Star, Trophy, Target, BookOpen,
  Wind, Sparkles, Calendar, CheckCircle2, Clock, LogOut, RefreshCw,
  AlertTriangle, Phone, MessageCircle, Loader2, Brain
} from 'lucide-react';
import ExplorerDashboard from './ExplorerDashboard';
import YoungAdultDashboard from './YoungAdultDashboard';
import AIStructuredExercises from './AIStructuredExercises';

// Emoji mood options for younger kids
const EMOJI_MOODS = [
  { value: 1, emoji: '😢', label: 'Really sad', color: 'bg-blue-500' },
  { value: 2, emoji: '😟', label: 'Worried', color: 'bg-blue-400' },
  { value: 3, emoji: '😕', label: 'Not great', color: 'bg-purple-400' },
  { value: 4, emoji: '😐', label: 'Okay', color: 'bg-gray-400' },
  { value: 5, emoji: '🙂', label: 'Pretty good', color: 'bg-yellow-400' },
  { value: 6, emoji: '😊', label: 'Good', color: 'bg-green-400' },
  { value: 7, emoji: '😄', label: 'Great!', color: 'bg-green-500' },
];

const YouthPortalDashboard = ({ onLogout, backendUrl }) => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [showMoodCheckin, setShowMoodCheckin] = useState(false);
  const [selectedMood, setSelectedMood] = useState(null);
  const [submittingMood, setSubmittingMood] = useState(false);
  const [copingExercises, setCopingExercises] = useState([]);
  const [crisisResources, setCrisisResources] = useState([]);
  
  const clientId = sessionStorage.getItem('youth_client_id');
  const firstName = sessionStorage.getItem('youth_first_name');
  const ageTier = sessionStorage.getItem('youth_age_tier');
  const features = JSON.parse(sessionStorage.getItem('youth_features') || '{}');
  const token = sessionStorage.getItem('youth_token');

  // Teen tier (13-15) - default dashboard below
  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${backendUrl}/api/youth-portal/dashboard?client_id=${clientId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, clientId, token]);

  const fetchCopingExercises = useCallback(async () => {
    try {
      const response = await fetch(
        `${backendUrl}/api/youth-portal/coping-exercises?age_tier=${ageTier}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCopingExercises(data.exercises || []);
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
    }
  }, [backendUrl, ageTier, token]);

  const fetchCrisisResources = useCallback(async () => {
    try {
      const response = await fetch(
        `${backendUrl}/api/youth-portal/crisis-resources`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCrisisResources(data.resources || []);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  }, [backendUrl, token]);

  const isTeen = ageTier !== 'explorer' && ageTier !== 'young_adult' && ageTier !== 'adult';

  useEffect(() => {
    // Only fetch data for the teen-tier dashboard. Explorer/Young Adult tiers
    // render their own dashboards via early-return below.
    if (!isTeen) return;
    fetchDashboard();
    fetchCopingExercises();
    fetchCrisisResources();
  }, [isTeen, fetchDashboard, fetchCopingExercises, fetchCrisisResources]);

  // Route to age-appropriate dashboards (must run AFTER all hooks above)
  // Explorer tier (10-12) - kid-friendly dashboard
  if (ageTier === 'explorer') {
    return <ExplorerDashboard onLogout={onLogout} backendUrl={backendUrl} />;
  }

  // Young Adult tier (16-17) - full features including AI companion, messaging, scheduling
  if (ageTier === 'young_adult' || ageTier === 'adult') {
    return <YoungAdultDashboard onLogout={onLogout} backendUrl={backendUrl} />;
  }

  const submitMoodCheckin = async () => {
    if (!selectedMood) return;
    
    setSubmittingMood(true);
    try {
      const response = await fetch(`${backendUrl}/api/mood-checkins/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: clientId,
          mood_score: selectedMood.value,
          emotions: [selectedMood.label],
          source: 'youth_portal'
        })
      });
      
      if (response.ok) {
        setShowMoodCheckin(false);
        setSelectedMood(null);
        fetchDashboard(); // Refresh dashboard
      }
    } catch (error) {
      console.error('Error submitting mood:', error);
    } finally {
      setSubmittingMood(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('youth_token');
    sessionStorage.removeItem('youth_client_id');
    sessionStorage.removeItem('youth_first_name');
    sessionStorage.removeItem('youth_age_tier');
    sessionStorage.removeItem('youth_features');
    onLogout();
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100" data-testid="youth-portal-dashboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">{getGreeting()}, {firstName}!</h1>
              <p className="text-sm text-gray-500">Let's make today great 🌟</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Streak Badge */}
            {dashboardData?.streak > 0 && (
              <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                <Flame className="w-3 h-3 mr-1" />
                {dashboardData.streak} day streak!
              </Badge>
            )}
            
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className={`grid bg-white/60 backdrop-blur-sm ${features.ai_structured_exercises ? 'grid-cols-5' : 'grid-cols-4'}`}>
            <TabsTrigger value="home" className="text-sm">
              <Heart className="w-4 h-4 mr-1" />
              Home
            </TabsTrigger>
            {features.ai_structured_exercises && (
              <TabsTrigger value="exercises" className="text-sm">
                <Brain className="w-4 h-4 mr-1" />
                Exercises
              </TabsTrigger>
            )}
            <TabsTrigger value="coping" className="text-sm">
              <Wind className="w-4 h-4 mr-1" />
              Coping
            </TabsTrigger>
            <TabsTrigger value="goals" className="text-sm">
              <Target className="w-4 h-4 mr-1" />
              Goals
            </TabsTrigger>
            <TabsTrigger value="help" className="text-sm">
              <Phone className="w-4 h-4 mr-1" />
              Help
            </TabsTrigger>
          </TabsList>

          {/* EXERCISES TAB - AI Structured Exercises for Teens */}
          {features.ai_structured_exercises && (
            <TabsContent value="exercises" className="space-y-4">
              <AIStructuredExercises 
                clientId={clientId}
                token={token}
                backendUrl={backendUrl}
              />
            </TabsContent>
          )}

          {/* HOME TAB */}
          <TabsContent value="home" className="space-y-6">
            {/* Mood Check-in Card */}
            <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white border-0 shadow-lg">
              <CardContent className="p-6">
                {!showMoodCheckin ? (
                  <div className="text-center">
                    <h3 className="text-xl font-semibold mb-2">How are you feeling today?</h3>
                    <p className="text-purple-100 mb-4">Taking a moment to check in helps you understand yourself better</p>
                    <Button 
                      onClick={() => setShowMoodCheckin(true)}
                      className="bg-white text-purple-600 hover:bg-purple-50"
                      data-testid="start-mood-checkin-btn"
                    >
                      Check In Now
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-center">Tap how you're feeling:</h3>
                    
                    <div className="flex justify-center gap-2 flex-wrap">
                      {EMOJI_MOODS.map((mood) => (
                        <button
                          key={mood.value}
                          onClick={() => setSelectedMood(mood)}
                          className={`text-4xl p-3 rounded-xl transition-all ${
                            selectedMood?.value === mood.value 
                              ? 'bg-white/30 scale-110 ring-2 ring-white' 
                              : 'hover:bg-white/20'
                          }`}
                          data-testid={`mood-emoji-${mood.value}`}
                        >
                          {mood.emoji}
                        </button>
                      ))}
                    </div>
                    
                    {selectedMood && (
                      <p className="text-center text-purple-100">
                        You selected: {selectedMood.label}
                      </p>
                    )}
                    
                    <div className="flex justify-center gap-3">
                      <Button 
                        variant="outline" 
                        onClick={() => { setShowMoodCheckin(false); setSelectedMood(null); }}
                        className="border-white/50 text-white hover:bg-white/20"
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={submitMoodCheckin}
                        disabled={!selectedMood || submittingMood}
                        className="bg-white text-purple-600 hover:bg-purple-50"
                        data-testid="submit-mood-btn"
                      >
                        {submittingMood ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gamification Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-white/80 backdrop-blur-sm">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
                    <Star className="w-6 h-6 text-purple-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {dashboardData?.gamification?.points || 0}
                  </p>
                  <p className="text-sm text-gray-500">Points</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/80 backdrop-blur-sm">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-2">
                    <Flame className="w-6 h-6 text-orange-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {dashboardData?.streak || 0}
                  </p>
                  <p className="text-sm text-gray-500">Day Streak</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/80 backdrop-blur-sm">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-2">
                    <Trophy className="w-6 h-6 text-yellow-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {dashboardData?.gamification?.badges?.length || 0}
                  </p>
                  <p className="text-sm text-gray-500">Badges</p>
                </CardContent>
              </Card>
            </div>

            {/* Homework/Tasks */}
            {dashboardData?.homework?.length > 0 && (
              <Card className="bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    Your Tasks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardData.homework.map((task, idx) => (
                      <div key={task.id || task.title || idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <BookOpen className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{task.title}</p>
                          {task.due_date && (
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Due: {new Date(task.due_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Mood History */}
            {dashboardData?.recent_moods?.length > 0 && (
              <Card className="bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Your Week</CardTitle>
                  <CardDescription>How you've been feeling</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between gap-2">
                    {[...Array(7)].map((_, idx) => {
                      const mood = dashboardData.recent_moods[6 - idx];
                      const moodEmoji = mood ? EMOJI_MOODS.find(m => m.value === Math.round(mood.mood_score))?.emoji || '😐' : '·';
                      return (
                        <div key={idx} className="text-center">
                          <div className="text-2xl">{moodEmoji}</div>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(Date.now() - (6 - idx) * 24 * 60 * 60 * 1000).toLocaleDateString('en', { weekday: 'short' })}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* COPING TAB */}
          <TabsContent value="coping" className="space-y-4">
            <div className="text-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Coping Tools</h2>
              <p className="text-gray-600">Exercises to help you feel better</p>
            </div>
            
            <div className="grid gap-4">
              {copingExercises.map((exercise) => (
                <Card key={exercise.id} className="bg-white/80 backdrop-blur-sm hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                        <Wind className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{exercise.title}</h3>
                        <p className="text-sm text-gray-600">{exercise.description}</p>
                        <Badge variant="outline" className="mt-2">
                          {Math.floor(exercise.duration_seconds / 60)} min
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* GOALS TAB */}
          <TabsContent value="goals" className="space-y-4">
            <div className="text-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Your Goals</h2>
              <p className="text-gray-600">Track your progress</p>
            </div>
            
            {dashboardData?.goals?.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.goals.map((goal, idx) => (
                  <Card key={goal.id || goal.title || idx} className="bg-white/80 backdrop-blur-sm">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <Target className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{goal.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">{goal.description}</p>
                          <div className="mt-3">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600">Progress</span>
                              <span className="font-medium text-gray-900">{goal.progress || 0}%</span>
                            </div>
                            <Progress value={goal.progress || 0} className="h-2" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-white/80 backdrop-blur-sm">
                <CardContent className="p-8 text-center">
                  <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No goals set yet. Talk to your therapist about setting some goals!</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* HELP TAB */}
          <TabsContent value="help" className="space-y-4">
            <Card className="bg-gradient-to-br from-red-500 to-orange-500 text-white border-0">
              <CardContent className="p-6 text-center">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">Need Help Right Now?</h2>
                <p className="text-red-100 mb-4">If you're in crisis or having thoughts of hurting yourself, please reach out.</p>
                <a href="tel:988">
                  <Button className="bg-white text-red-600 hover:bg-red-50 text-lg px-8 py-6">
                    <Phone className="w-5 h-5 mr-2" />
                    Call or Text 988
                  </Button>
                </a>
              </CardContent>
            </Card>

            <div className="space-y-3">
              {crisisResources.map((resource, idx) => (
                <Card key={resource.id || resource.name || idx} className="bg-white/80 backdrop-blur-sm">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900">{resource.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{resource.description}</p>
                    <div className="flex gap-3 mt-3">
                      {resource.phone && (
                        <a href={`tel:${resource.phone}`}>
                          <Button size="sm" variant="outline" className="text-purple-600 border-purple-200">
                            <Phone className="w-4 h-4 mr-1" />
                            Call
                          </Button>
                        </a>
                      )}
                      {resource.text && (
                        <Button size="sm" variant="outline" className="text-purple-600 border-purple-200">
                          <MessageCircle className="w-4 h-4 mr-1" />
                          Text
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default YouthPortalDashboard;
